import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AsyncButton } from "./ui/async-button.js";
import { DropdownSelect } from "./DropdownButton.js";
import { SpecularButton } from "./ui/specular-button.js";

interface EmployeeHit { id: string; firstName: string; lastName: string; employeeCode: string }

interface ImportRow {
  id: string; rowNumber: number; status: string;
  errorField: string | null; errorMessage: string | null;
  assetTag: string | null; category: string | null;
  rawEmployeeRef: string | null; matchMethod: string | null; matchConfidence: string | null;
  matchedEmployeeId: string | null; assignOnImport: boolean;
  resolvedAt: string | null;
  matchedEmployee: { id: string; firstName: string; lastName: string; employeeCode: string } | null;
  createdAsset: { id: string; assetTag: string; name: string } | null;
  rawData: Record<string, string>;
}
interface BatchDetail {
  id: string; fileName: string; status: string;
}

const inputStyle: React.CSSProperties = {
  padding: "6px 8px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "13px",
  background: "var(--color-surface-0)", boxSizing: "border-box",
};
const btnStyle = (color = "var(--color-brand-600)", text = "#fff"): React.CSSProperties => ({
  padding: "5px 12px", background: color, color: text,
  border: "none", borderRadius: "var(--radius-md)", cursor: "pointer",
  fontWeight: 600, fontSize: "12px",
});
const chip = (bg: string, fg: string): React.CSSProperties => ({
  display: "inline-block", fontSize: 11, padding: "3px 8px", borderRadius: 999, fontWeight: 700, background: bg, color: fg,
});

const STATUS_CHIP: Record<string, [string, string]> = {
  PENDING: ["var(--color-surface-100)", "var(--color-text-700)"],
  INVALID: ["var(--color-error-bg)", "var(--color-error-fg)"],
  DUPLICATE: ["var(--color-warning-bg)", "var(--color-warning-fg)"],
  IMPORTED: ["var(--color-success-bg)", "var(--color-success-fg)"],
  SKIPPED: ["var(--color-surface-100)", "var(--color-text-500)"],
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "INVALID", label: "Errors" },
  { key: "DUPLICATE", label: "Duplicates" },
  { key: "unmatched", label: "Unmatched employee" },
  { key: "IMPORTED", label: "Imported" },
  { key: "SKIPPED", label: "Skipped" },
] as const;

const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "CRITICAL"].map(p => ({ value: p, label: p }));

export function AssetImportReview({ batchId, onBack }: { batchId: string; onBack: () => void }) {
  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["key"]>("all");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [fixingId, setFixingId] = useState<string | null>(null);
  const [fixForm, setFixForm] = useState<Record<string, string>>({});

  const [empSearchFor, setEmpSearchFor] = useState<string | null>(null);
  const [empSearch, setEmpSearch] = useState("");
  const [empHits, setEmpHits] = useState<EmployeeHit[]>([]);
  const empRef = useRef<ReturnType<typeof setTimeout>>();

  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState("");
  const [raiseTicket, setRaiseTicket] = useState(false);
  const [ticketPriority, setTicketPriority] = useState("MEDIUM");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all" && filter !== "unmatched") params.set("status", filter);
      const { data } = await api.get<{ batch: BatchDetail; rows: ImportRow[] }>(`/asset-imports/${batchId}?${params}`);
      setBatch(data.batch);
      setRows(filter === "unmatched" ? data.rows.filter((r) => r.rawEmployeeRef && !r.matchedEmployeeId) : data.rows);
    } catch (e) {
      setErr(errMsg(e) ?? "Failed to load batch");
      logError("AssetImportReview.load", e);
    }
  }, [batchId, filter]);

  useEffect(() => { void load(); }, [load]);

  function onEmpSearch(v: string) {
    setEmpSearch(v);
    clearTimeout(empRef.current);
    if (!v.trim()) { setEmpHits([]); return; }
    empRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get<{ data: EmployeeHit[] }>(`/employees?search=${encodeURIComponent(v)}&limit=8`);
        setEmpHits(data.data);
      } catch (e) { logError("AssetImportReview.empSearch", e); }
    }, 250);
  }

  async function saveFix(rowId: string) {
    setSaving(true); setErr("");
    try {
      await api.patch(`/asset-imports/${batchId}/rows/${rowId}`, { patch: fixForm });
      setFixingId(null); setFixForm({});
      void load();
    } catch (e) { setErr(errMsg(e) ?? "Fix failed"); } finally { setSaving(false); }
  }

  async function assignEmployee(rowId: string, employeeId: string) {
    setSaving(true); setErr("");
    try {
      await api.patch(`/asset-imports/${batchId}/rows/${rowId}/match`, { employeeId });
      setEmpSearchFor(null); setEmpSearch(""); setEmpHits([]);
      void load();
    } catch (e) { setErr(errMsg(e) ?? "Assign failed"); } finally { setSaving(false); }
  }

  async function autoMatch() {
    setSaving(true); setErr("");
    try {
      const { data } = await api.post<{ matched: number; ambiguous: number; unmatched: number; total: number }>(`/asset-imports/${batchId}/auto-match`, {});
      setErr(`Auto-match: ${data.matched} matched, ${data.ambiguous} ambiguous, ${data.unmatched} unmatched (of ${data.total}).`);
      void load();
    } catch (e) { setErr(errMsg(e) ?? "Auto-match failed"); throw e; } finally { setSaving(false); }
  }

  async function submitResolve(rowId: string, action: "SKIP" | "ACKNOWLEDGE") {
    if (!resolveNotes.trim()) { setErr("Resolution notes are required"); return; }
    setSaving(true); setErr("");
    try {
      await api.post(`/asset-imports/${batchId}/rows/${rowId}/resolve`, {
        action, resolutionNotes: resolveNotes, raiseTicket, ticketPriority,
      });
      setResolvingId(null); setResolveNotes(""); setRaiseTicket(false); setTicketPriority("MEDIUM");
      void load();
    } catch (e) { setErr(errMsg(e) ?? "Resolve failed"); } finally { setSaving(false); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SpecularButton onClick={onBack}>&larr; Back</SpecularButton>
        <h3 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>{batch?.fileName ?? "Import batch"}</h3>
        <AsyncButton onClick={() => autoMatch()} disabled={saving} variant="outline" animated={false}>Auto-Match Employees</AsyncButton>
      </div>

      {err && <p style={{ color: "var(--color-text-600)", fontSize: 13, margin: 0 }}>{err}</p>}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button
            key={f.key} onClick={() => setFilter(f.key)}
            style={{ ...btnStyle(filter === f.key ? "var(--color-brand-600)" : "var(--color-surface-100)", filter === f.key ? "#fff" : "var(--color-text-700)") }}
          >{f.label}</button>
        ))}
      </div>

      <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
            {["Row", "Asset Tag", "Category", "Status", "Problem", "Employee", "Actions"].map((h) => (
              <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const [bg, fg] = STATUS_CHIP[r.status] ?? STATUS_CHIP["PENDING"]!;
              const isException = r.status === "INVALID" || r.status === "DUPLICATE";
              return (
                <Fragment key={r.id}>
                  <tr style={{ borderTop: "1px solid var(--color-surface-100)" }}>
                    <td style={{ padding: "8px 12px" }}>{r.rowNumber}</td>
                    <td style={{ padding: "8px 12px", fontFamily: "monospace" }}>{r.assetTag ?? "—"}</td>
                    <td style={{ padding: "8px 12px" }}>{r.category ?? "—"}</td>
                    <td style={{ padding: "8px 12px" }}><span style={chip(bg, fg)}>{r.status}</span></td>
                    <td style={{ padding: "8px 12px", maxWidth: 260 }}>{r.errorMessage ?? "—"}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {r.matchedEmployee
                        ? <span>{r.matchedEmployee.firstName} {r.matchedEmployee.lastName} <span style={{ fontSize: 11, color: "var(--color-text-400)" }}>({r.matchMethod})</span></span>
                        : r.rawEmployeeRef
                          ? <span style={{ color: "var(--color-text-400)" }}>Unmatched: {r.rawEmployeeRef}</span>
                          : "—"}
                    </td>
                    <td style={{ padding: "8px 12px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {isException && !r.resolvedAt && (
                        <button onClick={() => { setFixingId(fixingId === r.id ? null : r.id); setFixForm({}); }} style={btnStyle("var(--color-surface-100)", "var(--color-text-700)")}>Fix</button>
                      )}
                      {r.status !== "IMPORTED" && (
                        <button onClick={() => { setEmpSearchFor(empSearchFor === r.id ? null : r.id); setEmpSearch(""); setEmpHits([]); }} style={btnStyle("var(--color-surface-100)", "var(--color-text-700)")}>Assign to…</button>
                      )}
                      {isException && !r.resolvedAt && (
                        <button onClick={() => { setResolvingId(resolvingId === r.id ? null : r.id); setResolveNotes(""); }} style={btnStyle("var(--color-warning-bg)", "var(--color-warning-fg)")}>Resolve</button>
                      )}
                      {r.resolvedAt && <span style={{ fontSize: 11, color: "var(--color-text-400)" }}>Resolved</span>}
                    </td>
                  </tr>

                  {fixingId === r.id && (
                    <tr><td colSpan={7} style={{ padding: "10px 12px", background: "var(--color-surface-50)" }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: "var(--color-text-500)" }}>
                          {r.errorField ? `Fix "${r.errorField}":` : "Correct any field:"}
                        </span>
                        <input
                          style={{ ...inputStyle, width: 160 }}
                          placeholder={r.errorField ?? "field"}
                          defaultValue={r.errorField ? r.rawData[r.errorField] : ""}
                          onChange={(e) => setFixForm((f) => ({ ...f, [r.errorField ?? "notes"]: e.target.value }))}
                        />
                        <button disabled={saving} onClick={() => void saveFix(r.id)} style={btnStyle()}>Save</button>
                        <button onClick={() => setFixingId(null)} style={btnStyle("transparent", "var(--color-text-600)")}>Cancel</button>
                      </div>
                    </td></tr>
                  )}

                  {empSearchFor === r.id && (
                    <tr><td colSpan={7} style={{ padding: "10px 12px", background: "var(--color-surface-50)" }}>
                      <div style={{ position: "relative", maxWidth: 320 }}>
                        <input style={inputStyle} value={empSearch} onChange={(e) => onEmpSearch(e.target.value)} placeholder="Name or employee code…" autoFocus />
                        {empHits.length > 0 && (
                          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", zIndex: 10 }}>
                            {empHits.map((e) => (
                              <div key={e.id} onClick={() => void assignEmployee(r.id, e.id)} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
                                {e.firstName} {e.lastName} — <span style={{ fontFamily: "monospace", fontSize: 11 }}>{e.employeeCode}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td></tr>
                  )}

                  {resolvingId === r.id && (
                    <tr><td colSpan={7} style={{ padding: "10px 12px", background: "var(--color-surface-50)" }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <input style={{ ...inputStyle, flex: "1 1 240px" }} placeholder="Resolution notes (required)" value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} />
                        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                          <input type="checkbox" checked={raiseTicket} onChange={(e) => setRaiseTicket(e.target.checked)} /> Raise IT ticket
                        </label>
                        {raiseTicket && (
                          <DropdownSelect
                            value={ticketPriority}
                            onChange={setTicketPriority}
                            options={PRIORITY_OPTIONS}
                            placeholder="Priority"
                            ariaLabel="Ticket priority"
                            clearable={false}
                            open={openDropdown === `priority-${r.id}`}
                            onToggle={() => setOpenDropdown(d => (d === `priority-${r.id}` ? null : `priority-${r.id}`))}
                            onClose={() => setOpenDropdown(d => (d === `priority-${r.id}` ? null : d))}
                          />
                        )}
                        <button disabled={saving} onClick={() => void submitResolve(r.id, "SKIP")} style={btnStyle("var(--color-surface-100)", "var(--color-text-700)")}>Skip row</button>
                        <button disabled={saving} onClick={() => void submitResolve(r.id, "ACKNOWLEDGE")} style={btnStyle()}>Acknowledge</button>
                        <button onClick={() => setResolvingId(null)} style={btnStyle("transparent", "var(--color-text-600)")}>Cancel</button>
                      </div>
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <p style={{ fontSize: 13, color: "var(--color-text-400)", padding: 20 }}>No rows match this filter.</p>}
      </div>
    </div>
  );
}
